import { Link, useLocation } from "react-router-dom";
import { FlaskIcon, PlusIcon, AtomIcon, SignOutIcon, UsersIcon, UserCircleIcon } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import AdminAuditDialog from "@/components/AdminAuditDialog";
import { useAuth } from "@/context/AuthContext";

export default function AppHeader() {
  const loc = useLocation();
  const { user, hasRole, logout } = useAuth();
  const onLogin = loc.pathname === "/login";

  if (onLogin) {
    return (
      <header className="w-full border-b border-[var(--border)] bg-white h-16 flex items-center px-6 md:px-10" data-testid="app-header-minimal">
        <Link to="/" className="flex items-center gap-3">
          <div className="h-8 w-8 bg-[var(--primary)] flex items-center justify-center">
            <FlaskIcon size={18} weight="bold" color="#fff" />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-heading text-[15px] font-semibold tracking-tight">EU Glossary</span>
            <span className="label-kicker mt-0.5">Reg. 2025/1175</span>
          </div>
        </Link>
      </header>
    );
  }

  return (
    <header className="w-full border-b border-[var(--border)] bg-white sticky top-0 z-30" data-testid="app-header">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
          <div className="h-8 w-8 bg-[var(--primary)] flex items-center justify-center">
            <FlaskIcon size={18} weight="bold" color="#fff" />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-heading text-[15px] font-semibold tracking-tight">EU Glossary</span>
            <span className="label-kicker mt-0.5">Reg. 2025/1175</span>
          </div>
        </Link>

        {user && (
          <nav className="flex items-center gap-1">
            <Link
              to="/"
              className={`px-3 py-2 text-xs font-mono uppercase tracking-[0.18em] ${loc.pathname === "/" ? "text-[var(--primary)]" : "text-[var(--text-muted)] hover:text-[var(--text)]"}`}
              data-testid="nav-directory"
            >
              Directory
            </Link>
            <Link
              to="/formulations"
              className={`px-3 py-2 text-xs font-mono uppercase tracking-[0.18em] inline-flex items-center gap-1.5 ${loc.pathname.startsWith("/formulations") ? "text-[var(--primary)]" : "text-[var(--text-muted)] hover:text-[var(--text)]"}`}
              data-testid="nav-formulations"
            >
              <AtomIcon size={13} weight="regular" /> Formulations
            </Link>
            {hasRole("admin") && (
              <>
                <div className="mx-1 h-5 w-px bg-[var(--border)]" />
                <AdminAuditDialog />
              </>
            )}
            {hasRole("editor") && (
              <Link to="/new" data-testid="nav-new-ingredient">
                <Button className="btn-primary rounded-none h-9 px-4 gap-2 font-mono text-[11px] uppercase tracking-[0.18em] ml-1">
                  <PlusIcon size={14} weight="bold" /> New Ingredient
                </Button>
              </Link>
            )}
            <div className="ml-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="inline-flex items-center gap-2 h-9 px-3 border border-[var(--border)] hover:bg-[var(--surface)] font-mono text-[11px] uppercase tracking-[0.14em]" data-testid="user-menu">
                    <UserCircleIcon size={16} />
                    <span>{user.name || user.email.split("@")[0]}</span>
                    <span className="chip text-[9px]" style={{ padding: "0 4px" }}>{user.role}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="rounded-none" align="end">
                  <DropdownMenuLabel className="font-mono text-[10px] text-[var(--text-muted)]">{user.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {hasRole("admin") && (
                    <DropdownMenuItem asChild data-testid="menu-users">
                      <Link to="/users"><UsersIcon size={14} className="mr-2" /> Team & Roles</Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                    <SignOutIcon size={14} className="mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
